import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { Button, Drawer, EmptyState, Field, Input, Select } from "../components/ui";
import { useToast } from "../components/Toast";
import {
  ApiError,
  addVendorAlias,
  autoConfirmVendors,
  confirmVendor,
  getVendor,
  listVendorMerges,
  listVendors,
  mergeVendors,
  promoteVendor,
  removeVendorAlias,
  seedCtppEligibility,
  setVendorCtpp,
} from "../lib/api";
import type {
  Vendor,
  VendorDetail,
  VendorMergeConflict,
  VendorMergeRecord,
} from "../lib/types";
import { formatApiError } from "../lib/utils";

/**
 * Vendor administration (plan section 5.10).
 *
 * Three states an operator has to be able to see and change:
 *
 *   status     provisional | confirmed | merged
 *   routable   whether ANY alias is a FlashText routing keyword.  Under
 *              ORION1_MENTIONS_V3 extraction creates vendors with
 *              routable = false on purpose, so a vendor can sit in the
 *              database, be named by evidence, and still route nothing.
 *              POST /api/vendors/{id}/promote is the only way out of that,
 *              which is why it is a first-class column and action here.
 *   CTPP       eligible / not eligible / not decided, always with the
 *              provenance and verified-at that make the claim auditable.
 *
 * `ctpp_eligible` is `boolean | null` and the null is load-bearing: "nobody
 * has decided" is not "not eligible".  Nothing on this page collapses it.
 */

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "provisional", label: "Provisional" },
  { value: "confirmed", label: "Confirmed" },
  { value: "merged", label: "Merged" },
];

const ROUTABLE_FILTERS = [
  { value: "", label: "Routable: any" },
  { value: "true", label: "Routable only" },
  { value: "false", label: "Non-routable only" },
];

const PAGE_SIZE = 50;

function statusChipClass(status: string): string {
  if (status === "confirmed") return "border-secondary/40 bg-secondary/10 text-secondary";
  if (status === "merged") return "border-outline-variant bg-surface-variant/50 text-on-surface-variant";
  return "border-tertiary/40 bg-tertiary/10 text-tertiary";
}

function Chip({
  children,
  className = "",
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 border font-data-label text-[10px] uppercase tracking-wide ${className}`}
    >
      {children}
    </span>
  );
}

function RoutableChip({ vendor }: { vendor: Pick<Vendor, "routable" | "routable_alias_count"> }) {
  if (vendor.routable) {
    return (
      <Chip
        className="border-primary/40 bg-primary/10 text-primary"
        title={`${vendor.routable_alias_count} alias(es) are FlashText routing keywords`}
      >
        <Icon name="alt_route" className="text-xs" />
        Routable · {vendor.routable_alias_count}
      </Chip>
    );
  }
  return (
    <Chip
      className="border-error/50 bg-error/10 text-error"
      title="No alias is a routing keyword — evidence naming this vendor will not route to it"
    >
      <Icon name="block" className="text-xs" />
      Not routable
    </Chip>
  );
}

function CtppCell({ vendor }: { vendor: Pick<Vendor, "ctpp_eligible" | "ctpp_parent_name" | "ctpp_provenance" | "ctpp_verified_at"> }) {
  const { ctpp_eligible: eligible } = vendor;
  const chip =
    eligible === null || eligible === undefined ? (
      <Chip className="border-outline-variant text-on-surface-variant" title="No CTPP decision recorded — this is not the same as 'not eligible'">
        Not decided
      </Chip>
    ) : eligible ? (
      <Chip className="border-secondary/40 bg-secondary/10 text-secondary">CTPP eligible</Chip>
    ) : (
      <Chip className="border-outline-variant bg-surface-variant/40 text-on-surface-variant">
        Not eligible
      </Chip>
    );
  return (
    <div className="flex flex-col gap-1 min-w-0">
      {chip}
      {vendor.ctpp_parent_name ? (
        <span className="text-[11px] font-data-mono text-on-surface-variant truncate">
          parent: {vendor.ctpp_parent_name}
        </span>
      ) : null}
      <span className="text-[10px] font-data-mono text-outline truncate">
        {vendor.ctpp_provenance
          ? `provenance: ${vendor.ctpp_provenance}`
          : "provenance: none recorded"}
        {vendor.ctpp_verified_at ? ` · verified ${vendor.ctpp_verified_at.slice(0, 10)}` : " · never verified"}
      </span>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="border border-error/50 bg-error/10 text-error px-4 py-3 flex items-start gap-3"
    >
      <Icon name="error" className="text-base shrink-0" />
      <p className="text-body-sm flex-1 min-w-0 break-words">{message}</p>
      {onRetry ? (
        <Button variant="ghost" size="sm" onClick={onRetry} className="shrink-0 text-error">
          Retry
        </Button>
      ) : null}
    </div>
  );
}

function ConflictList({ conflicts }: { conflicts: VendorMergeConflict[] }) {
  if (conflicts.length === 0) return null;
  return (
    <div className="border border-tertiary/50 bg-tertiary/10 p-3 space-y-2">
      <p className="font-data-label text-data-label uppercase text-tertiary flex items-center gap-2">
        <Icon name="warning" className="text-sm" />
        {conflicts.length} project{conflicts.length === 1 ? "" : "s"} not re-homed
      </p>
      <p className="text-body-sm text-on-surface-variant">
        The merge kept these projects on the absorbed vendor because the survivor already owns a
        project with the same slug (uq_projects_target_contractor_slug). Merge or rename the
        blocking project, then re-run the vendor merge.
      </p>
      <ul className="space-y-1">
        {conflicts.map((c) => (
          <li
            key={c.project_id}
            className="font-data-mono text-[11px] text-on-surface bg-surface-container-lowest border border-outline-variant px-2 py-1.5"
          >
            <span className="text-on-surface">{c.project_slug || c.project_id}</span>
            <span className="text-outline"> — kept contractor </span>
            <span className="text-tertiary">{c.kept_main_contractor || "—"}</span>
            <span className="text-outline"> · blocked by </span>
            <span className="text-on-surface-variant">{c.blocking_project_id}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function VendorAdminPage() {
  const { toast } = useToast();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");
  const [routable, setRoutable] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [busyId, setBusyId] = useState("");

  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<VendorDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [mergeOpen, setMergeOpen] = useState(false);
  const [survivorId, setSurvivorId] = useState("");
  const [absorbedId, setAbsorbedId] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeError, setMergeError] = useState("");
  const [mergeConflicts, setMergeConflicts] = useState<VendorMergeConflict[] | null>(null);
  const [mergeSummary, setMergeSummary] = useState("");
  const [mergeHistory, setMergeHistory] = useState<VendorMergeRecord[]>([]);
  const [mergeHistoryError, setMergeHistoryError] = useState("");

  const [minSources, setMinSources] = useState("");
  const [bulkBusy, setBulkBusy] = useState("");

  /* ------------------------------------------------------------------ list */

  const loadVendors = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const data = await listVendors({
        status: status || undefined,
        search: search || undefined,
        routable: routable === "" ? undefined : routable === "true",
        limit: PAGE_SIZE,
        offset,
      });
      setVendors(data.vendors || []);
      setTotal(Number(data.total || 0));
    } catch (e) {
      setVendors([]);
      setTotal(0);
      setListError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, search, routable, offset]);

  useEffect(() => {
    void loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadMergeHistory = useCallback(async () => {
    setMergeHistoryError("");
    try {
      const data = await listVendorMerges({ limit: 25 });
      setMergeHistory(data.merges || []);
    } catch (e) {
      setMergeHistory([]);
      setMergeHistoryError(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (mergeOpen) void loadMergeHistory();
  }, [mergeOpen, loadMergeHistory]);

  /* ---------------------------------------------------------------- detail */

  const loadDetail = useCallback(async (vendorId: string) => {
    if (!vendorId) return;
    setDetailLoading(true);
    setDetailError("");
    try {
      const data = await getVendor(vendorId);
      setDetail(data.vendor);
    } catch (e) {
      setDetail(null);
      setDetailError(formatApiError(e));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError("");
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  /* --------------------------------------------------------------- actions */

  const runRowAction = useCallback(
    async (vendorId: string, label: string, action: () => Promise<unknown>) => {
      setBusyId(vendorId);
      try {
        await action();
        toast(`${label} — done`);
        await loadVendors();
        if (selectedId === vendorId) await loadDetail(vendorId);
      } catch (e) {
        toast(formatApiError(e), true);
      } finally {
        setBusyId("");
      }
    },
    [toast, loadVendors, loadDetail, selectedId],
  );

  const runMerge = useCallback(async () => {
    if (!survivorId || !absorbedId) {
      setMergeError("Pick both a survivor and an absorbed vendor.");
      return;
    }
    if (survivorId === absorbedId) {
      setMergeError("A vendor cannot absorb itself.");
      return;
    }
    setMergeBusy(true);
    setMergeError("");
    setMergeConflicts(null);
    setMergeSummary("");
    try {
      const data = await mergeVendors({
        survivor_vendor_id: survivorId,
        absorbed_vendor_id: absorbedId,
      });
      // The `conflicts` list is the whole point of the response: a merge that
      // moved 4 of 6 projects and says nothing about the other 2 is a lie.
      setMergeConflicts(data.conflicts || []);
      setMergeSummary(
        `${data.projects_moved} project(s) moved · ${data.merge.vendor_aliases} alias(es) · ` +
          `${data.merge.project_participations} participation(s) · ${data.merge.clusters} cluster(s) · ` +
          `${data.merge.reconciliation_decisions_reopened} decision(s) reopened · ` +
          `${data.conflict_count} conflict(s)`,
      );
      toast(
        data.conflict_count > 0
          ? `Merged with ${data.conflict_count} unresolved conflict(s)`
          : "Vendors merged",
        data.conflict_count > 0,
      );
      setAbsorbedId("");
      await loadVendors();
      await loadMergeHistory();
      if (selectedId) await loadDetail(selectedId);
    } catch (e) {
      const conflicts =
        e instanceof ApiError && Array.isArray((e.body as { conflicts?: unknown })?.conflicts)
          ? ((e.body as { conflicts: VendorMergeConflict[] }).conflicts as VendorMergeConflict[])
          : null;
      if (conflicts) setMergeConflicts(conflicts);
      setMergeError(formatApiError(e));
    } finally {
      setMergeBusy(false);
    }
  }, [survivorId, absorbedId, toast, loadVendors, loadMergeHistory, loadDetail, selectedId]);

  const vendorLabel = useCallback(
    (vendorId: string) =>
      vendors.find((v) => v.vendor_id === vendorId)?.display_name || vendorId || "—",
    [vendors],
  );

  const provisionalCount = useMemo(
    () => vendors.filter((v) => v.status === "provisional").length,
    [vendors],
  );
  const nonRoutableCount = useMemo(() => vendors.filter((v) => !v.routable).length, [vendors]);

  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;

  return (
    <main className="flex flex-col flex-1 overflow-hidden bg-surface-dim min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col custom-scrollbar">
        <PageTitle
          title="Vendor administration"
          meta="Confirm provisional vendors, promote them into keyword routing, merge duplicates, and record CTPP eligibility with provenance."
          actions={
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={bulkBusy !== ""}
                onClick={async () => {
                  setBulkBusy("seed");
                  try {
                    const data = await seedCtppEligibility();
                    toast(`CTPP seed: ${data.vendors_updated} vendor(s) updated`);
                    await loadVendors();
                  } catch (e) {
                    toast(formatApiError(e), true);
                  } finally {
                    setBulkBusy("");
                  }
                }}
              >
                <Icon name="verified" className="text-base" />
                {bulkBusy === "seed" ? "Seeding…" : "Seed CTPP list"}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setMergeOpen(true)}
                aria-expanded={mergeOpen}
                aria-controls="vendor-merge-panel"
              >
                <Icon name="merge" className="text-base" />
                Merge vendors
              </Button>
            </>
          }
        />

        {/* ------------------------------------------------------- filters -- */}
        <section className="px-6 py-3 border-b border-outline-variant bg-surface-container-low flex flex-wrap items-end gap-3">
          <Field label="Search" className="w-64">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Vendor name or alias…"
            />
          </Field>
          <Field label="Status" className="w-44">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
            >
              {STATUS_FILTERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Routing" className="w-48">
            <Select
              value={routable}
              onChange={(e) => {
                setRoutable(e.target.value);
                setOffset(0);
              }}
            >
              {ROUTABLE_FILTERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>

          <div className="flex items-end gap-2 ml-auto">
            <Field label="Auto-confirm: min independent sources" className="w-56">
              <Input
                type="number"
                min={1}
                value={minSources}
                onChange={(e) => setMinSources(e.target.value)}
                placeholder="unset → confirms nothing"
              />
            </Field>
            <Button
              variant="secondary"
              size="md"
              disabled={bulkBusy !== ""}
              onClick={async () => {
                setBulkBusy("auto");
                try {
                  const parsed = Number.parseInt(minSources, 10);
                  const data = await autoConfirmVendors(
                    Number.isFinite(parsed) && parsed > 0 ? parsed : undefined,
                  );
                  toast(
                    data.vendors_confirmed === 0
                      ? "Auto-confirm confirmed 0 vendors — with no threshold and ORION1_VENDOR_CONFIRM_MIN_SOURCES unset this is the documented default, not a failure."
                      : `${data.vendors_confirmed} vendor(s) confirmed`,
                  );
                  await loadVendors();
                } catch (e) {
                  toast(formatApiError(e), true);
                } finally {
                  setBulkBusy("");
                }
              }}
            >
              {bulkBusy === "auto" ? "Running…" : "Auto-confirm"}
            </Button>
          </div>
        </section>

        {/* --------------------------------------------------------- table -- */}
        <section className="flex-1 min-h-0 px-6 py-4 space-y-3">
          {listError ? <ErrorBanner message={listError} onRetry={() => void loadVendors()} /> : null}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-on-surface-variant">
            <span className="font-data-mono">
              {loading ? "Loading…" : `${total} vendor${total === 1 ? "" : "s"}`}
            </span>
            {!loading && vendors.length > 0 ? (
              <>
                <span className="text-outline">·</span>
                <span>{provisionalCount} provisional on this page</span>
                <span className="text-outline">·</span>
                <span className={nonRoutableCount > 0 ? "text-error" : undefined}>
                  {nonRoutableCount} not routable on this page
                </span>
              </>
            ) : null}
          </div>

          {!loading && !listError && vendors.length === 0 ? (
            <EmptyState
              className="border border-outline-variant rounded-lg bg-surface-container-high"
              title={search || status || routable ? "No vendors match these filters" : "No vendors yet"}
              description={
                search || status || routable
                  ? "Clear the search, status or routing filter to see the full register."
                  : "Vendors appear once extraction or the v3 backfill creates them. Auto-created vendors arrive provisional and non-routable."
              }
            >
              {search || status || routable ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mb-4"
                  onClick={() => {
                    setSearchInput("");
                    setStatus("");
                    setRoutable("");
                    setOffset(0);
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </EmptyState>
          ) : null}

          {vendors.length > 0 ? (
            <div className="border border-outline-variant bg-surface-container overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="bg-surface-container-high text-left">
                    {["Vendor", "Status", "Routing", "Aliases", "CTPP", "Clusters", ""].map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 font-data-label text-data-label uppercase text-on-surface-variant whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v) => (
                    <tr
                      key={v.vendor_id}
                      className="border-t border-outline-variant/60 align-top hover:bg-surface-container-high/50"
                    >
                      <td className="px-3 py-2 min-w-[14rem]">
                        <button
                          type="button"
                          className="text-left text-primary hover:underline font-semibold"
                          onClick={() => setSelectedId(v.vendor_id)}
                        >
                          {v.display_name || v.canonical_name || v.vendor_id}
                        </button>
                        <div className="font-data-mono text-[10px] text-outline truncate">
                          {v.canonical_name}
                        </div>
                        {v.merged_into_vendor_id ? (
                          <div className="font-data-mono text-[10px] text-on-surface-variant">
                            merged into {v.merged_into_vendor_id}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <Chip className={statusChipClass(v.status)}>{v.status}</Chip>
                      </td>
                      <td className="px-3 py-2">
                        <RoutableChip vendor={v} />
                      </td>
                      <td className="px-3 py-2 font-data-mono tabular-nums whitespace-nowrap">
                        {v.alias_count}
                        <span className="text-outline"> total</span>
                        <br />
                        <span className={v.routable_alias_count > 0 ? "text-primary" : "text-error"}>
                          {v.routable_alias_count} routing
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-[16rem]">
                        <CtppCell vendor={v} />
                      </td>
                      <td className="px-3 py-2 font-data-mono tabular-nums">{v.cluster_count}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col items-stretch gap-1.5 min-w-[10rem]">
                          {v.status === "provisional" ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busyId === v.vendor_id}
                              onClick={() =>
                                void runRowAction(v.vendor_id, "Vendor confirmed", () =>
                                  confirmVendor(v.vendor_id),
                                )
                              }
                            >
                              Confirm
                            </Button>
                          ) : null}
                          {!v.routable ? (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={busyId === v.vendor_id}
                              title="Promote every alias to a FlashText routing keyword — the only way a v3-created vendor enters keyword routing"
                              onClick={() =>
                                void runRowAction(v.vendor_id, "Vendor promoted to routable", () =>
                                  promoteVendor(v.vendor_id),
                                )
                              }
                            >
                              Promote to routable
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedId(v.vendor_id)}
                          >
                            Details
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {total > PAGE_SIZE ? (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPrev || loading}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <span className="font-data-mono text-body-sm text-on-surface-variant tabular-nums">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNext || loading}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </section>
      </div>

      {/* ------------------------------------------------------ merge panel -- */}
      <Drawer
        id="vendor-merge-panel"
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        icon="merge"
        title="Merge vendors"
        subtitle="Aliases, participations, clusters and evidence move to the survivor. Projects that would collide keep their vendor and are reported."
      >
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Survivor (kept)">
              <Select value={survivorId} onChange={(e) => setSurvivorId(e.target.value)}>
                <option value="">Select…</option>
                {vendors.map((v) => (
                  <option key={v.vendor_id} value={v.vendor_id}>
                    {v.display_name || v.canonical_name} ({v.status})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Absorbed (folded in)">
              <Select value={absorbedId} onChange={(e) => setAbsorbedId(e.target.value)}>
                <option value="">Select…</option>
                {vendors.map((v) => (
                  <option key={v.vendor_id} value={v.vendor_id}>
                    {v.display_name || v.canonical_name} ({v.status})
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <p className="text-body-sm text-on-surface-variant">
            Only vendors on the current page are listed. Filter or search the register to bring a
            vendor into range.
          </p>

          {mergeError ? <ErrorBanner message={mergeError} /> : null}

          <Button
            variant="primary"
            disabled={mergeBusy || !survivorId || !absorbedId}
            onClick={() => void runMerge()}
          >
            {mergeBusy ? "Merging…" : `Merge ${vendorLabel(absorbedId)} into ${vendorLabel(survivorId)}`}
          </Button>

          {mergeSummary ? (
            <div className="border border-outline-variant bg-surface-container p-3">
              <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                Merge result
              </p>
              <p className="font-data-mono text-body-sm text-on-surface mt-1">{mergeSummary}</p>
            </div>
          ) : null}

          {mergeConflicts !== null ? (
            mergeConflicts.length > 0 ? (
              <ConflictList conflicts={mergeConflicts} />
            ) : (
              <p className="text-body-sm text-secondary flex items-center gap-2">
                <Icon name="check_circle" className="text-sm" />
                No project conflicts — every project moved to the survivor.
              </p>
            )
          ) : null}

          <div className="space-y-2">
            <p className="font-data-label text-data-label uppercase text-on-surface-variant">
              Recent vendor merges
            </p>
            {mergeHistoryError ? <ErrorBanner message={mergeHistoryError} onRetry={() => void loadMergeHistory()} /> : null}
            {!mergeHistoryError && mergeHistory.length === 0 ? (
              <p className="text-body-sm text-on-surface-variant">No vendor merges recorded yet.</p>
            ) : null}
            {mergeHistory.map((m) => (
              <div
                key={`${m.absorbed_vendor_id}:${m.survivor_vendor_id}`}
                className="border border-outline-variant bg-surface-container px-3 py-2 font-data-mono text-[11px]"
              >
                <span className="text-on-surface-variant">{m.absorbed_display_name}</span>
                <span className="text-outline"> → </span>
                <span className="text-on-surface">{m.survivor_display_name}</span>
                <span className="text-outline"> · {m.absorbed_status}</span>
              </div>
            ))}
          </div>
        </div>
      </Drawer>

      {/* ----------------------------------------------------- detail panel -- */}
      <Drawer
        id="vendor-detail-panel"
        open={Boolean(selectedId)}
        onClose={() => setSelectedId("")}
        icon="badge"
        title={detail?.display_name || "Vendor"}
        subtitle={detail ? detail.canonical_name : selectedId}
      >
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-5">
          {detailLoading ? (
            <p className="text-body-sm text-on-surface-variant">Loading vendor…</p>
          ) : null}
          {detailError ? (
            <ErrorBanner message={detailError} onRetry={() => void loadDetail(selectedId)} />
          ) : null}
          {detail ? (
            <VendorDetailBody
              detail={detail}
              busy={busyId === detail.vendor_id}
              onAction={(label, action) => runRowAction(detail.vendor_id, label, action)}
            />
          ) : null}
        </div>
      </Drawer>
    </main>
  );
}

/* ========================================================================== */

function VendorDetailBody({
  detail,
  busy,
  onAction,
}: {
  detail: VendorDetail;
  busy: boolean;
  onAction: (label: string, action: () => Promise<unknown>) => Promise<void>;
}) {
  const [aliasText, setAliasText] = useState("");
  const [aliasRoutable, setAliasRoutable] = useState(false);
  const [ctppEligible, setCtppEligible] = useState<string>(
    detail.ctpp_eligible === null || detail.ctpp_eligible === undefined
      ? "unknown"
      : detail.ctpp_eligible
        ? "true"
        : "false",
  );
  const [ctppParent, setCtppParent] = useState(detail.ctpp_parent_name || "");
  const [ctppProvenance, setCtppProvenance] = useState(detail.ctpp_provenance || "operator");
  const [ctppConfirm, setCtppConfirm] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Chip className={statusChipClass(detail.status)}>{detail.status}</Chip>
        <RoutableChip vendor={detail} />
        <Chip className="border-outline-variant text-on-surface-variant">
          {detail.participations.length} participation(s)
        </Chip>
        <Chip className="border-outline-variant text-on-surface-variant">
          {detail.clusters.length} cluster(s)
        </Chip>
      </div>

      {!detail.routable ? (
        <div className="border border-error/50 bg-error/10 p-3">
          <p className="text-body-sm text-error font-semibold">Not in keyword routing</p>
          <p className="text-body-sm text-on-surface-variant mt-1">
            No alias of this vendor is a FlashText keyword, so evidence naming it will not route
            to it. Under ORION1_MENTIONS_V3 extraction creates vendors this way on purpose;
            promoting is an operator decision.
          </p>
          <Button
            variant="primary"
            size="sm"
            className="mt-2"
            disabled={busy}
            onClick={() =>
              void onAction("Vendor promoted to routable", () => promoteVendor(detail.vendor_id))
            }
          >
            Promote to routable
          </Button>
        </div>
      ) : null}

      {detail.status === "provisional" ? (
        <div className="border border-tertiary/40 bg-tertiary/10 p-3">
          <p className="text-body-sm text-tertiary font-semibold">Provisional vendor</p>
          <p className="text-body-sm text-on-surface-variant mt-1">
            Created without operator review. Confirming also promotes its aliases into routing.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            disabled={busy}
            onClick={() =>
              void onAction("Vendor confirmed", () => confirmVendor(detail.vendor_id))
            }
          >
            Confirm vendor
          </Button>
        </div>
      ) : null}

      {/* ------------------------------------------------------------ CTPP -- */}
      <section className="border border-outline-variant bg-surface-container p-3 space-y-3">
        <p className="font-data-label text-data-label uppercase text-on-surface-variant">
          CTPP eligibility
        </p>
        <div className="text-body-sm text-on-surface-variant">
          Recorded:{" "}
          <span className="font-data-mono text-on-surface">
            {detail.ctpp_eligible === null || detail.ctpp_eligible === undefined
              ? "not decided"
              : detail.ctpp_eligible
                ? "eligible"
                : "not eligible"}
          </span>{" "}
          · provenance{" "}
          <span className="font-data-mono text-on-surface">
            {detail.ctpp_provenance || "none recorded"}
          </span>{" "}
          · verified{" "}
          <span className="font-data-mono text-on-surface">
            {detail.ctpp_verified_at || "never"}
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Eligible">
            <Select value={ctppEligible} onChange={(e) => setCtppEligible(e.target.value)}>
              <option value="unknown">Not decided</option>
              <option value="true">Eligible</option>
              <option value="false">Not eligible</option>
            </Select>
          </Field>
          <Field label="Provenance">
            <Input
              value={ctppProvenance}
              onChange={(e) => setCtppProvenance(e.target.value)}
              placeholder="operator | seed | evidence URL"
            />
          </Field>
          <Field label="CTPP parent name" className="sm:col-span-2">
            <Input
              value={ctppParent}
              onChange={(e) => setCtppParent(e.target.value)}
              placeholder="Parent listed on the designated CTPP register"
            />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-body-sm text-on-surface-variant">
          <input
            type="checkbox"
            checked={ctppConfirm}
            onChange={(e) => setCtppConfirm(e.target.checked)}
          />
          Stamp verified-at (records this as a verified check, not a guess)
        </label>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() =>
            void onAction("CTPP eligibility saved", () =>
              setVendorCtpp(detail.vendor_id, {
                eligible: ctppEligible === "unknown" ? null : ctppEligible === "true",
                parent_name: ctppParent,
                provenance: ctppProvenance,
                confirm: ctppConfirm,
              }),
            )
          }
        >
          Save CTPP eligibility
        </Button>
      </section>

      {/* ---------------------------------------------------------- aliases -- */}
      <section className="border border-outline-variant bg-surface-container p-3 space-y-3">
        <p className="font-data-label text-data-label uppercase text-on-surface-variant">
          Aliases ({detail.aliases.length})
        </p>
        {detail.aliases.length === 0 ? (
          <p className="text-body-sm text-on-surface-variant">
            No aliases recorded — nothing can resolve to this vendor by name.
          </p>
        ) : (
          <ul className="space-y-1">
            {detail.aliases.map((a) => (
              <li
                key={a.alias_id}
                className="flex items-center gap-2 border border-outline-variant/60 bg-surface-container-lowest px-2 py-1.5"
              >
                <span className="font-data-mono text-body-sm text-on-surface flex-1 min-w-0 truncate">
                  {a.alias}
                </span>
                {a.routable ? (
                  <Chip className="border-primary/40 text-primary">routing</Chip>
                ) : (
                  <Chip className="border-outline-variant text-outline">recognise only</Chip>
                )}
                <span className="font-data-mono text-[10px] text-outline shrink-0">
                  {a.source || "—"}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 hover:text-error"
                  disabled={busy}
                  onClick={() =>
                    void onAction("Alias removed", () =>
                      removeVendorAlias(detail.vendor_id, a.alias_id),
                    )
                  }
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Add alias" className="flex-1 min-w-[12rem]">
            <Input
              value={aliasText}
              onChange={(e) => setAliasText(e.target.value)}
              placeholder="Alternate spelling or legal name"
            />
          </Field>
          <label className="flex items-center gap-2 text-body-sm text-on-surface-variant pb-2">
            <input
              type="checkbox"
              checked={aliasRoutable}
              onChange={(e) => setAliasRoutable(e.target.checked)}
            />
            routing keyword
          </label>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !aliasText.trim()}
            className="mb-0.5"
            onClick={() =>
              void onAction("Alias added", async () => {
                const result = await addVendorAlias(detail.vendor_id, {
                  alias: aliasText.trim(),
                  routable: aliasRoutable,
                });
                setAliasText("");
                setAliasRoutable(false);
                return result;
              })
            }
          >
            Add
          </Button>
        </div>
        <p className="text-[11px] text-on-surface-variant">
          A normalized alias already owned by another vendor comes back as a conflict — merge the
          vendors instead of forcing the name.
        </p>
      </section>

      {/* --------------------------------------------------- participations -- */}
      <section className="border border-outline-variant bg-surface-container p-3 space-y-2">
        <p className="font-data-label text-data-label uppercase text-on-surface-variant">
          Project participations
        </p>
        {detail.participations.length === 0 ? (
          <p className="text-body-sm text-on-surface-variant">
            No project participation recorded for this vendor.
          </p>
        ) : (
          detail.participations.map((p) => (
            <div
              key={`${p.project_id}:${p.cluster_id}`}
              className="border border-outline-variant/60 bg-surface-container-lowest px-2 py-1.5 text-body-sm"
            >
              <span className="text-on-surface">{p.project_name || p.project_id}</span>
              <span className="text-outline"> · role </span>
              <span className="font-data-mono text-on-surface-variant">{p.role || "mentioned"}</span>
              {p.uncertainty ? <span className="text-tertiary"> · uncertain</span> : null}
              <span className="text-outline"> · {p.project_status || "—"}</span>
              <span className="text-outline"> · decided by {p.decided_by || "—"}</span>
            </div>
          ))
        )}
      </section>

      {detail.merged_from.length > 0 ? (
        <section className="border border-outline-variant bg-surface-container p-3 space-y-2">
          <p className="font-data-label text-data-label uppercase text-on-surface-variant">
            Merged into this vendor
          </p>
          {detail.merged_from.map((m) => (
            <div key={m.vendor_id} className="font-data-mono text-[11px] text-on-surface-variant">
              {m.display_name} <span className="text-outline">({m.canonical_name})</span>
            </div>
          ))}
        </section>
      ) : null}
    </>
  );
}
